import { useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderIcon from '@mui/icons-material/Folder';
import { useQuery } from '@tanstack/react-query';
import { fetchBreakdown, type DiskEntry } from '../../api/disk';

interface Props {
  open: boolean;
  mountPoint: string;
  onClose: () => void;
}

function fmtBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export function DiskBreakdownDrawer({ open, mountPoint, onClose }: Props) {
  const [currentPath, setCurrentPath] = useState<string>(mountPoint);
  const [bustFlag, setBustFlag] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['disk-breakdown', currentPath, bustFlag],
    queryFn: () => fetchBreakdown(currentPath, bustFlag),
    enabled: open && !!currentPath,
    staleTime: 10 * 60 * 1000,
  });

  function handleRefresh() {
    setBustFlag((v) => !v);
  }

  function handleNavigate(entry: DiskEntry) {
    if (!entry.path) return;
    setCurrentPath(entry.path);
    setBustFlag(false);
  }

  // Reset to mount root when drawer opens with a new mount point
  const effectivePath = open ? currentPath : mountPoint;

  // Build breadcrumb segments
  const segments = effectivePath.split('/').filter(Boolean);
  const breadcrumbs: Array<{ label: string; path: string }> = [{ label: '/', path: '/' }];
  segments.forEach((seg, i) => {
    breadcrumbs.push({ label: seg, path: '/' + segments.slice(0, i + 1).join('/') });
  });

  const totalBytes = data?.total_bytes ?? 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, p: 0 } }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ flex: 1 }}>Disk Breakdown</Typography>
        <Tooltip title="Refresh (clears cache)">
          <IconButton size="small" onClick={handleRefresh} disabled={isFetching}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ ml: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2, py: 1.5 }}>
        <Breadcrumbs maxItems={4} sx={{ fontSize: '0.82rem' }}>
          {breadcrumbs.map((b, i) =>
            i < breadcrumbs.length - 1 ? (
              <Link
                key={b.path}
                component="button"
                underline="hover"
                color="inherit"
                sx={{ fontSize: '0.82rem', cursor: 'pointer' }}
                onClick={() => setCurrentPath(b.path)}
              >
                {b.label}
              </Link>
            ) : (
              <Typography key={b.path} sx={{ fontSize: '0.82rem' }} color="text.primary">
                {b.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
        {data && (
          <Typography variant="caption" color="text.secondary">
            Total: {fmtBytes(data.total_bytes)} · scanned {new Date(data.scanned_at).toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {(isLoading || isFetching) && <LinearProgress sx={{ mx: 2 }} />}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">
              Scanning… large filesystems can take over a minute.
            </Typography>
          </Stack>
        </Box>
      )}

      {data && (
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {data.entries.map((entry) => {
            const pct = totalBytes > 0 ? (entry.size_bytes / totalBytes) * 100 : 0;
            const isDir = !!entry.path && entry.name !== '(other)';
            return (
              <Box
                key={entry.name}
                sx={{
                  px: 2,
                  py: 1,
                  cursor: isDir ? 'pointer' : 'default',
                  '&:hover': isDir ? { bgcolor: 'action.hover' } : {},
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
                onClick={() => isDir && handleNavigate(entry)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {isDir && <FolderIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: isDir ? 'monospace' : undefined, fontSize: '0.82rem' }}
                    >
                      {entry.name}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {fmtBytes(entry.size_bytes)} ({pct.toFixed(1)}%)
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(pct, 100)}
                  sx={{ height: 4, borderRadius: 1 }}
                />
              </Box>
            );
          })}
          {data.entries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No entries found (directory may be empty or unreadable).
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button size="small" onClick={() => setCurrentPath(mountPoint)}>
          Back to root ({mountPoint})
        </Button>
      </Box>
    </Drawer>
  );
}
